import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { ChevronRight, Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ThemeToggle } from '@/components/dashboard/theme-toggle'
import { NotificationsMenu } from '@/components/dashboard/notifications-menu'
import { UserMenu } from '@/components/dashboard/user-menu'
import { useDashboard } from '@/components/dashboard/dashboard-provider'
import { getNavItem } from '@/constants/navigation'

export const Topbar: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const { collapsed, toggleCollapsed, setMobileNavOpen, setCommandOpen, isMac } = useDashboard()
    const current = getNavItem(pathname)
    const title = current?.label ?? 'Halaman tidak ditemukan'
    const collapseLabel = collapsed ? 'Lebarkan sidebar' : 'Ciutkan sidebar'

    return (
        <header className='sticky top-0 z-30 border-b bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70'>
            <div className='flex h-16 items-center gap-2 px-4 sm:px-6 lg:px-8'>
                <Button
                    variant='ghost'
                    size='icon-lg'
                    className='-ml-1.5 lg:hidden'
                    onClick={() => setMobileNavOpen(true)}
                    aria-label='Buka menu navigasi'
                >
                    <Menu className='size-5' />
                </Button>

                <Tooltip>
                    <TooltipTrigger
                        render={(
                            <Button
                                variant='ghost'
                                size='icon-lg'
                                className='-ml-2 hidden lg:inline-flex'
                                onClick={toggleCollapsed}
                                aria-label={collapseLabel}
                            />
                        )}
                    >
                        {collapsed ? <PanelLeftOpen className='size-[18px]' /> : <PanelLeftClose className='size-[18px]' />}
                    </TooltipTrigger>

                    <TooltipContent side='bottom'>
                        {collapseLabel}
                        <span className='ml-1.5 font-mono opacity-70'>
                            {isMac ? '⌘' : 'Ctrl'} B
                        </span>
                    </TooltipContent>
                </Tooltip>

                <div className='flex min-w-0 items-center gap-1.5 text-sm'>
                    <span className='hidden text-muted-foreground sm:inline'>
                        Arunika
                    </span>
                    <ChevronRight className='hidden size-3.5 text-muted-foreground/70 sm:block' aria-hidden='true' />
                    <span className='truncate font-medium text-foreground'>
                        {title}
                    </span>
                </div>

                <div className='ml-auto flex items-center gap-1 sm:gap-1.5'>
                    <button
                        type='button'
                        onClick={() => setCommandOpen(true)}
                        className='hidden h-9 w-60 items-center gap-2 rounded-lg border bg-card px-3 text-sm text-muted-foreground shadow-xs transition-colors outline-none hover:border-foreground/20 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 md:flex xl:w-72'
                    >
                        <Search className='size-4 shrink-0' aria-hidden='true' />
                        <span className='flex-1 truncate text-left'>
                            Cari halaman atau pelanggan
                        </span>
                        <kbd className='rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground'>
                            {isMac ? '⌘' : 'Ctrl'} K
                        </kbd>
                    </button>

                    <Button
                        variant='ghost'
                        size='icon-lg'
                        className='md:hidden'
                        onClick={() => setCommandOpen(true)}
                        aria-label='Cari halaman atau pelanggan'
                    >
                        <Search className='size-[18px]' />
                    </Button>

                    <ThemeToggle />
                    <NotificationsMenu />

                    <div className='ml-1 h-6 w-px bg-border' aria-hidden='true' />

                    <UserMenu />
                </div>
            </div>
        </header>
    )
}
